// ==========================
// Version 4 — pages/AdminMemories.js
// - Adds slide-over AdminMemoryDrawer
// - Click title or "Details" to open drawer
// - Keeps Hide/Unhide/Delete + filters + paging
// ==========================
import React from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  adminHideMemory,
  adminUnhideMemory,
  adminDeleteMemory,
} from '../db';
import AdminMemoryDrawer from './AdminMemoryDrawer';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';
const ROSE_CLAY = '#B76E79';

const PAGE_SIZE = 12;    // client-side page size
const FETCH_LIMIT = 200; // how many docs we pull from Firestore per refresh

export default function AdminMemories() {
  const { currentUser } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [qtxt, setQtxt] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [err, setErr] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);
  const [pubFilter, setPubFilter] = React.useState('all'); // all | public | private

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerId, setDrawerId] = React.useState(null);
  const [drawerInitial, setDrawerInitial] = React.useState(null);

  // fetch latest memories
  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const qy = query(
          collection(db, 'memories'),
          orderBy('createdAt', 'desc'),
          limit(FETCH_LIMIT)
        );
        const snap = await getDocs(qy);
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || data.date || 'Untitled',
            userId: data.userId || '',
            isPublic: !!data.isPublic,
            isMediaPublic: !!data.isMediaPublic,
            createdAt: data.createdAt?.toDate?.() || null,
          };
        });
        if (alive) setRows(list);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AdminMemories] load failed:', e);
        if (alive) setErr('Could not load memories. Check Firestore indexes/permissions.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshNonce]);

  // filter (text + public/private)
  const filtered = React.useMemo(() => {
    const t = qtxt.trim().toLowerCase();
    let base = rows;
    if (pubFilter === 'public') base = base.filter(r => r.isPublic === true);
    if (pubFilter === 'private') base = base.filter(r => r.isPublic === false);

    if (!t) return base;
    return base.filter((r) =>
      r.id.toLowerCase().includes(t) ||
      (r.title || '').toLowerCase().includes(t) ||
      (r.userId || '').toLowerCase().includes(t)
    );
  }, [rows, qtxt, pubFilter]);

  // paging
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  // handlers
  const doRefresh = () => setRefreshNonce((n) => n + 1);
  const first = () => setPage(1);
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(totalPages, p + 1));

  // keep page in range when filters change
  React.useEffect(() => {
    setPage(1);
  }, [qtxt, pubFilter]);

  // moderation actions (optimistic)
  async function doHide(r) {
    if (!currentUser?.uid) return;
    setBusyId(r.id);
    setErr('');
    const prev = r;
    setRows((list) => list.map(x => x.id === r.id ? { ...x, isPublic: false, isMediaPublic: false } : x));
    try {
      await adminHideMemory({ memoryId: r.id, actorUid: currentUser.uid, ownerUid: r.userId });
    } catch (e) {
      // rollback
      setRows((list) => list.map(x => x.id === r.id ? prev : x));
      setErr(e?.message || 'Failed to hide memory. Check Firestore rules.');
    } finally {
      setBusyId(null);
    }
  }

  async function doUnhide(r) {
    if (!currentUser?.uid) return;
    setBusyId(r.id);
    setErr('');
    const prev = r;
    setRows((list) => list.map(x => x.id === r.id ? { ...x, isPublic: true, isMediaPublic: true } : x));
    try {
      await adminUnhideMemory({ memoryId: r.id, actorUid: currentUser.uid, ownerUid: r.userId });
    } catch (e) {
      setRows((list) => list.map(x => x.id === r.id ? prev : x));
      setErr(e?.message || 'Failed to unhide memory. Check Firestore rules.');
    } finally {
      setBusyId(null);
    }
  }

  async function doDelete(r) {
    if (!currentUser?.uid) return;
    if (!window.confirm('Delete this memory and its media? This cannot be undone.')) return;
    setBusyId(r.id);
    setErr('');
    const prevRows = rows;
    setRows((list) => list.filter(x => x.id !== r.id));
    try {
      await adminDeleteMemory({ memoryId: r.id, actorUid: currentUser.uid, ownerUid: r.userId });
    } catch (e) {
      // rollback
      setRows(prevRows);
      setErr(e?.message || 'Failed to delete memory. Check Firestore rules.');
    } finally {
      setBusyId(null);
    }
  }

  function openDrawer(r) {
    setDrawerInitial(r);
    setDrawerId(r.id);
    setDrawerOpen(true);
  }

  function applyDrawerUpdate(partial) {
    setRows((list) => list.map(x => x.id === drawerId ? { ...x, ...partial } : x));
  }

  function handleDrawerDeleted() {
    setRows((list) => list.filter(x => x.id !== drawerId));
  }

  return (
    <>
      <div style={headerRow}>
        <h1 style={h1}>Memories</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value)}
            style={select}
            aria-label="Filter by visibility"
          >
            <option value="all">All</option>
            <option value="public">Public only</option>
            <option value="private">Private only</option>
          </select>
          <button type="button" onClick={first} style={pillBtn}>First</button>
          <button type="button" onClick={prev} style={pillBtn}>Prev</button>
          <button type="button" onClick={next} style={pillBtn}>Next</button>
          <button type="button" onClick={doRefresh} style={pillBtn}>Refresh</button>
        </div>
      </div>

      {err ? <div style={errBox}>{err}</div> : null}

      <div style={{ marginBottom: 12 }}>
        <input
          placeholder="Search by id, title or uid…"
          value={qtxt}
          onChange={(e) => setQtxt(e.target.value)}
          style={input}
          aria-label="Search memories"
        />
        <div style={{ color: WARM_GRAY, fontSize: 12, marginTop: 6 }}>
          Page {safePage} · Showing {pageRows.length} of {filtered.length} filtered (from {rows.length} loaded)
        </div>
      </div>

      <div style={table}>
        <div style={thead}>
          <div>Title</div>
          <div>Owner UID</div>
          <div>Public</div>
          <div>Media</div>
          <div>Open</div>
          <div>Actions</div>
        </div>

        {loading ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Loading…</div>
        ) : pageRows.length === 0 ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>No memories found.</div>
        ) : (
          pageRows.map((r) => (
            <div key={r.id} style={trow}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <button
                  type="button"
                  onClick={() => openDrawer(r)}
                  style={titleBtn}
                  title="View details"
                >
                  {r.title}
                </button>
                {r.createdAt && (
                  <span style={{ color: WARM_GRAY, fontSize: 12 }}>
                    {r.createdAt.toLocaleString()}
                  </span>
                )}
              </div>
              <code style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.userId}
              </code>
              <div>{r.isPublic ? 'Yes' : 'No'}</div>
              <div>{r.isMediaPublic ? 'Yes' : 'No'}</div>
              <div>
                <Link to={`/m/${r.id}`} style={link} target="_blank" rel="noopener noreferrer">
                  Open ↗
                </Link>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {r.isPublic ? (
                  <button
                    type="button"
                    style={btnTiny}
                    disabled={busyId === r.id}
                    onClick={() => doHide(r)}
                    title="Set isPublic=false and isMediaPublic=false"
                  >
                    Hide
                  </button>
                ) : (
                  <button
                    type="button"
                    style={btnTiny}
                    disabled={busyId === r.id}
                    onClick={() => doUnhide(r)}
                    title="Set isPublic=true and isMediaPublic=true"
                  >
                    Unhide
                  </button>
                )}
                <button
                  type="button"
                  style={{ ...btnTiny, borderColor: ROSE_CLAY, color: BURGUNDY }}
                  disabled={busyId === r.id}
                  onClick={() => doDelete(r)}
                  title="Delete memory and its media"
                >
                  Delete
                </button>
                <button
                  type="button"
                  style={btnTiny}
                  onClick={() => openDrawer(r)}
                  title="View details drawer"
                >
                  Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Drawer */}
      <AdminMemoryDrawer
        open={drawerOpen}
        memoryId={drawerId}
        initialRow={drawerInitial}
        onClose={() => setDrawerOpen(false)}
        onUpdated={(partial) => applyDrawerUpdate(partial)}
        onDeleted={() => handleDrawerDeleted()}
      />
    </>
  );
}

const h1 = { margin: 0, fontFamily: 'serif', fontSize: 26 };
const headerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 };
const pillBtn = { background: GOLD, color: NAVY, border: 'none', padding: '6px 10px', borderRadius: 10, fontWeight: 800, cursor: 'pointer' };
const select = { background: '#fff', color: NAVY, border: `1px solid ${GOLD}`, borderRadius: 10, padding: '6px 10px', fontWeight: 800 };

const input = { width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(253,252,249,0.2)', background: '#fff' };

const table = { display: 'grid', gap: 6 };
const thead = {
  display: 'grid',
  gridTemplateColumns: '2fr 1.4fr 90px 90px 90px 1.6fr',
  gap: 8,
  fontWeight: 800,
  color: IVORY,
  background: 'rgba(253,252,249,0.06)',
  padding: 10,
  borderRadius: 10,
};
const trow = {
  display: 'grid',
  gridTemplateColumns: '2fr 1.4fr 90px 90px 90px 1.6fr',
  gap: 8,
  background: '#fff',
  color: NAVY,
  padding: 10,
  borderRadius: 10,
  alignItems: 'center',
};
const link = {
  color: NAVY,
  textDecoration: 'none',
  fontWeight: 800,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  background: '#fff',
};
const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};
const errBox = {
  color: BURGUNDY,
  background: 'rgba(92,26,43,0.08)',
  border: '1px dashed rgba(183,110,121,0.55)',
  padding: 10,
  borderRadius: 10,
  margin: '8px 0',
};
const titleBtn = {
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  textAlign: 'left',
  fontWeight: 800,
  fontSize: 15,
  cursor: 'pointer',
  color: NAVY,
};
// ==========================
// End Version 4 — pages/AdminMemories.js
// ==========================
